import Vue from "vue";
import axios from "axios";
import moment from "moment";
import { generateUniqueTimeslotId } from "../../utilities/timeslotUniqueId";
import { get, find, groupBy, reduce } from "lodash";

const NUMBER_OF_DAYS = 15;

/**
 * The Store Pattern
 * actions -> mutations
 * Never mutate the store directly.
 */

const EMPTY_TIMESLOTS = {
  date: {},
  items: [],
  pickup: {},
  delivery: {},
  uniqueSlots: {},
  numDays: NUMBER_OF_DAYS
};

// initial state
// state.timeslots
const state = {
  ...EMPTY_TIMESLOTS,
  isFetchingTimeslots: false
};

const getters = {
  timeslotDates: state => get(state, "date", {}),
  timeslotDatesSorted: (state, getters) =>
    Object.keys(getters.timeslotDates).sort(),
  timeslotsDateByDateString: (state, getters) => dateString =>
    get(getters.timeslotDates, dateString, {}),
  timeslotsGetDateCount: state => get(state, "numDays"),
  timeslotsLoading: state => state.isFetchingTimeslots,
  timeslotAllSlots: state => get(state, "uniqueSlots", {}),
  timeslotById: (state, getters) => uniqueSlotId =>
    get(getters.timeslotAllSlots, uniqueSlotId, {}),
  timeslotAllowsAlcohol: (state, getters) => uniqueSlotId =>
    !!get(getters.timeslotById(uniqueSlotId), "allowAlcohol", false),

  timeslotsNoDatesHaveTimeslots: state => fulfillmentType => {
    return !Object.keys(get(state, [fulfillmentType], {})).length;
  },
  timeslotsIdsByFulfillmentAndDate: state => (fulfillmentType, dateString) => {
    return get(state, [fulfillmentType, dateString], []);
  },
  timeslotsByFulfillmentAndDate: (state, getters) => (
    fulfillmentType,
    dateString
  ) => {
    return get(state, [fulfillmentType, dateString], []).map(slotId => {
      const uniqueSlotId = generateUniqueTimeslotId(dateString, slotId);
      return getters.timeslotById(uniqueSlotId);
    });
  },
  timeslotsIdsSortedByTimeOfDay: (state, getters) => (
    fulfillmentType,
    dateString
  ) => {
    const timeslots = getters.timeslotsByFulfillmentAndDate(
      fulfillmentType,
      dateString
    );
    //Filter first, then sort, then map timeslot ids.
    const filterByPeriod = isPeriod =>
      timeslots
        .filter(a => {
          return isPeriod(moment.parseZone(a.startTime).hour());
        })
        .sort((a, b) => {
          return moment.parseZone(a.startTime).diff(moment.parseZone(b.startTime));
        })
        .map(t => t.id);

    // COVID: May need to make all hours after 5pm evening
    // and remove or update the extended functionality
    const morning = filterByPeriod(hour => hour >= 0 && hour < 12);
    const afternoon = filterByPeriod(hour => hour >= 12 && hour < 17);
    const evening = filterByPeriod(hour => hour >= 17 && hour < 24);

    const timeslotsByTimeOfDay = [];

    // Only add if timeslots exist for the time of day
    const addTimeslotsToPeriod = (timeOfDay, timeslots) => {
      if (timeslots.length > 0) {
        timeslotsByTimeOfDay.push({
          display: timeOfDay,
          slots: timeslots
        });
      }
    };

    addTimeslotsToPeriod("Morning", morning);
    addTimeslotsToPeriod("Afternoon", afternoon);
    addTimeslotsToPeriod("Evening", evening);

    return timeslotsByTimeOfDay;
  },
  timeslotsHasSlotsForFulfillmentAndDate: (state, getters) => (
    fulfillmentType,
    dateString
  ) => {
    return !!getters.timeslotsByFulfillmentAndDate(fulfillmentType, dateString)
      .length;
  },
  timeslotsFirstDayWithTimeslotsForFulfillment: (
    state,
    getters
  ) => fulfillmentType => {
    return find(getters.timeslotDatesSorted, dateString => {
      return getters.timeslotsHasSlotsForFulfillmentAndDate(
        fulfillmentType,
        dateString
      );
    });
  },
  timeslotsFirstAvailableForDate: (state, getters) => (
    fulfillmentType,
    dateString
  ) => {
    // assumes that timeslots for the given day are already in a sorted order
    return get(
      getters.timeslotsIdsByFulfillmentAndDate(fulfillmentType, dateString),
      0,
      null
    );
  },
  timeslotsLowestPriceForDate: (state, getters) => (
    fulfillmentType,
    dateString
  ) => {
    const timeslots = getters.timeslotsByFulfillmentAndDate(
      fulfillmentType,
      dateString
    );
    return timeslots.reduce((lowestPrice, timeslot) => {
      const { totalPrice } = timeslot;
      const lowestPriceSet = lowestPrice !== null;
      const curPriceIsNumber = typeof totalPrice === "number";
      if (curPriceIsNumber) {
        if (lowestPriceSet) {
          return lowestPrice < totalPrice ? lowestPrice : totalPrice;
        }
        return totalPrice;
      }
      return lowestPrice;
    }, null);
  },
  timeslotsShopperFeeMinForDate: (state, getters) => (
    fulfillmentType,
    dateString
  ) => {
    const timeslots = getters.timeslotsByFulfillmentAndDate(
      fulfillmentType,
      dateString
    );
    return timeslots.reduce((minShopPrice, timeslots) => {
      const { shopPrice } = timeslots;
      const minShopPriceSet = minShopPrice !== null;
      const curPriceIsNumber = typeof shopPrice === "number";
      if (curPriceIsNumber) {
        if (minShopPriceSet) {
          return minShopPrice < shopPrice ? minShopPrice : shopPrice;
        }
        return shopPrice;
      }
      return minShopPrice;
    }, null);
  },
  timeslotsShopperFeeMaxForDate: (state, getters) => (
    fulfillmentType,
    dateString
  ) => {
    const timeslots = getters.timeslotsByFulfillmentAndDate(
      fulfillmentType,
      dateString
    );
    return timeslots.reduce((maxShopPrice, timeslot) => {
      const { shopPrice } = timeslot;
      const maxShopPriceSet = maxShopPrice !== null;
      const curPriceIsNumber = typeof shopPrice === "number";
      if (curPriceIsNumber) {
        if (maxShopPriceSet) {
          return maxShopPrice > shopPrice ? maxShopPrice : shopPrice;
        }
        return shopPrice;
      }
      return maxShopPrice;
    }, null);
  }
};

// actions - can be async
const actions = {
  /** load all timeslots for this store */
  LOAD_ALL_TIMESLOTS() {
    const vm = this;
    vm.commit("errors/CLEAR_ERRORS");

    const { fulfillmentType, store } = this.state.reservation;
    // COVID: Remove once healthcare program is done
    const { isHealthcareWorker } = this.state.profile;

    // this should ALWAYS exist...
    const storeId = store ? store.id : "";

    // but just in case
    if (!storeId) {
      console.error(
        "CANNOT RETRIEVE TIMESLOTS: NO STORE ID FOR THIS RESERVATION"
      );
      return;
    }

    // COVID: Remove and update query string once healthcare program is done
    const includeMedical = !!(store.supportsMedTimeslot && isHealthcareWorker);

    vm.commit("timeslots/SET_FETCHING_TIMESLOTS", true);

    return axios
      .all([
        axios.get(
          `/commerce-api/v1/timeslot/timeslots?store_id=${storeId}&days=${NUMBER_OF_DAYS}&fulfillment_type=${fulfillmentType}${includeMedical ? "&includeMedical=true" : ""}`
        )
      ])
      .then(
        axios.spread(timeslotData => {
          const { status, data } = timeslotData;

          vm.commit("timeslots/SET_FAVOR_ELIGIBLE", get(data, "favorDeliveryEligible", false));
          vm.commit("timeslots/SET_NUM_DAYS", get(data, "overrideNumberOfDays", NUMBER_OF_DAYS));
          vm.commit("timeslots/SET_DATE_LIST", data);
          vm.commit("timeslots/SET_ALL", data);
          vm.commit("timeslots/SET_SLOTS_BY_ID");
          vm.commit("timeslots/SET_LISTS_BY_TYPE");
          vm.commit("reservation/UPDATE_EXPIRATION", data.currentTime);
          return status;
        })
      )
      .catch(error => {
        const timeslotError = { ...error, type: "timeslotPicker" };
        const status = get(timeslotError, "response.status");

        if (status == 404) {
          // Add a more human-friendly error to the UI for 404 error
          timeslotError.response.data.title =
            fulfillmentType == "pickup"
              ? "No pickup times available at this store yet"
              : "No delivery times available at this location yet";
          timeslotError.variant = "warning";
        }
        vm.commit("timeslots/CLEAR_TIMESLOTS");
        vm.dispatch("errors/HANDLE_ERROR", timeslotError);
      })
      .finally(() => {
        vm.commit("timeslots/SET_FETCHING_TIMESLOTS", false);
      });
  }
};

// mutations - Must be synchronous
const mutations = {
  /** set whether Favor Two Hour Devlivery is available */
  SET_FAVOR_ELIGIBLE(state, favorDeliveryEligible) {
    Vue.set(state, "favorDeliveryEligible", favorDeliveryEligible);
  },

  /** save the number of days that timeslots are being returned for */
  SET_NUM_DAYS(state, numDays) {
    Vue.set(state, "numDays", numDays);
  },

  /** store a list of all dates, with relevant info */
  SET_DATE_LIST(state, data) {
    // console.log('SET_DATE_LIST()', data)
    const { currentTime } = data;

    // main object
    const obj = {};

    // cache today & tomorrow, for comparisons
    const today = moment(currentTime);
    const tomorrow = moment(currentTime).add(1, "days");

    // loop thru days to add relevant info
    let i = 0;
    while (i < state.numDays) {
      const date = moment(currentTime).add(i++, "days");
      const key = date.format("YYYY-MM-DD");

      obj[key] = {
        val: key, // probly don't need this
        isToday: moment(date).isSame(today),
        isTomorrow: moment(date).isSame(tomorrow),
        dayOfWeek: moment(date).format("dddd"),
        dayOfWeekShort: moment(date).format("ddd"),
        month: moment(date).format("MMMM"),
        monthShort: moment(date).format("MMM"),
        date: moment(date).format("DD"),
        dateShort: moment(date).format("D")
      };
    }

    // set date list obj onto the state
    Vue.set(state, "date", obj);
  },

  /** store a list of all timeslots, with relevant info */
  SET_ALL(state, data) {
    // console.log('SET_ALL()', data)

    // set items onto the state
    Vue.set(state, "items", data.items);
  },

  /** create an object with a list of timeslots, by timeslot id (for easy lookup in the template) */
  SET_SLOTS_BY_ID(state) {
    // console.log('SET_SLOTS_BY_ID()')
    const { items } = state;

    const uniqueSlots = items.reduce((acc, item) => {
      const id = get(item, ["timeslot", "id"]);
      const date = get(item, ["timeslot", "date"]);
      const uniqueId = generateUniqueTimeslotId(date, id);

      acc[uniqueId] = get(item, "timeslot", {});
      return acc;
    }, {});
    Vue.set(state, "uniqueSlots", uniqueSlots);
  },

  SET_LISTS_BY_TYPE(state) {
    // console.log('SET_LISTS_BY_TYPE()')
    const { items } = state;

    // main object
    const obj = {
      pickup: [],
      delivery: []
    };

    // loop thru the timeslot items
    items.forEach(item => {
      const {
        date,
        timeslot: { id, fulfillmentType }
      } = item;

      // make sure we have a date list for this fulfillment type
      if (!obj[fulfillmentType]) {
        obj[fulfillmentType] = {};
      }

      // make sure we have a date in this date list
      const thisDate = obj[fulfillmentType][date] || [];

      // add this id to this date
      thisDate.push(id);

      // overwrite this date back onto the main object
      obj[fulfillmentType][date] = thisDate;
    });

    // loop thru fulfillment types
    Object.keys(obj).forEach(type => {
      // cache the list of dates for this type
      const dateList = obj[type];

      // loop thru list of dates
      Object.keys(dateList).forEach(date => {
        // sort timeslot ids for this date
        obj[type][date] = dateList[date].sort();
      });

      // set this type onto the state
      Vue.set(state, type, obj[type]);
    });
  },
  SET_FETCHING_TIMESLOTS(state, isFetching) {
    Vue.set(state, "isFetchingTimeslots", isFetching);
  },

  CLEAR_TIMESLOTS(state) {
    this._vm.$set(this.state, "timeslots", {
      ...EMPTY_TIMESLOTS,
      isFetchingTimeslots: state.isFetchingTimeslots
    });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
